export interface IStorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

class MemoryStorageAdapter implements IStorageAdapter {
  private store = new Map<string, string>();

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }
}

class WebStorageAdapter implements IStorageAdapter {
  private readonly backend: Storage;

  constructor(backend: Storage) {
    this.backend = backend;
  }

  getItem(key: string): string | null {
    try {
      return this.backend.getItem(key);
    } catch {
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      this.backend.setItem(key, value);
    } catch {
      // Storage unavailable (e.g. private browsing quota) — fail silently, caller falls back to re-auth.
    }
  }

  removeItem(key: string): void {
    try {
      this.backend.removeItem(key);
    } catch {
      // no-op
    }
  }

  clear(): void {
    try {
      this.backend.clear();
    } catch {
      // no-op
    }
  }
}

export const memoryStorage: IStorageAdapter = new MemoryStorageAdapter();

export const localStorageAdapter: IStorageAdapter = new WebStorageAdapter(window.localStorage);

export const sessionStorageAdapter: IStorageAdapter = new WebStorageAdapter(window.sessionStorage);

export type StorageStrategy = 'memory' | 'localStorage' | 'sessionStorage';

export function resolveStorageAdapter(strategy: StorageStrategy): IStorageAdapter {
  switch (strategy) {
    case 'localStorage':
      return localStorageAdapter;
    case 'sessionStorage':
      return sessionStorageAdapter;
    case 'memory':
    default:
      return memoryStorage;
  }
}
